/**
 * Header — susmies
 * - Toggles "is-scrolled" on transparent sticky headers past a threshold.
 * - Handles mobile menu drawer open/close.
 * - Auto-submits locale/currency selectors on change.
 */
(function () {
  'use strict';

  // --------------------------------------------------------------------
  // Scrolled state for transparent sticky header
  // --------------------------------------------------------------------
  const initScrollState = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    const isTransparent = header.dataset.transparent === 'true';
    const isSticky = header.dataset.sticky === 'true';
    if (!isTransparent || !isSticky) return;

    const threshold = 40;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > threshold);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  // --------------------------------------------------------------------
  // Announcement-bar height tracking (solid sticky headers)
  // Solid (non-home) pages stick the whole header-group, offset upwards
  // by the announcement bar's height so it slides off on scroll while
  // the header stays pinned. We measure it and expose a CSS variable.
  // --------------------------------------------------------------------
  const initAnnouncementHeight = () => {
    const group = document.querySelector('.shopify-section-group-header-group');
    if (!group) return;
    if (group.querySelector('.susmies-header--transparent')) return;

    const announcement = group.querySelector('.announcement-bar-wrapper, .announcement-bar');
    if (!announcement) return;

    const update = () => {
      const h = announcement.offsetHeight;
      group.style.setProperty('--susmies-announcement-height', `${h}px`);
    };
    update();

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(update);
      ro.observe(announcement);
    } else {
      window.addEventListener('resize', update, { passive: true });
    }
  };

  // --------------------------------------------------------------------
  // Mobile subnav — slide-in menu strip below the main header once the
  // user scrolls past the configured trigger section. Mobile-only.
  // --------------------------------------------------------------------
  const initSubnav = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    const subnav = header.querySelector('[data-subnav]');
    if (!subnav) return;

    const triggerSelector = subnav.dataset.subnavTrigger;
    if (!triggerSelector) return;

    const trigger = document.querySelector(triggerSelector);
    if (!trigger) return;

    const inner = header.querySelector('.susmies-header__inner');
    const mq = window.matchMedia('(max-width: 990px)');

    let visible = false;
    const setVisible = (next) => {
      if (next === visible) return;
      visible = next;
      header.classList.toggle('is-subnav-visible', next);
      subnav.setAttribute('aria-hidden', next ? 'false' : 'true');
    };

    const update = () => {
      if (!mq.matches) {
        setVisible(false);
        return;
      }
      const headerHeight = inner ? inner.offsetHeight : 56;
      const passed = trigger.getBoundingClientRect().top <= headerHeight;
      setVisible(passed);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  };

  // --------------------------------------------------------------------
  // Mobile menu drawer
  // --------------------------------------------------------------------
  const initMobileMenu = () => {
    const menu = document.querySelector('[data-mobile-menu]');
    if (!menu) return;

    const toggles = document.querySelectorAll('[data-mobile-menu-toggle]');
    const closers = menu.querySelectorAll('[data-mobile-menu-close]');

    let lockedScrollY = 0;

    const lockScroll = () => {
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${lockedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    };

    const unlockScroll = () => {
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      window.scrollTo(0, lockedScrollY);
    };

    const nav = menu.querySelector('[data-mobile-mega]');
    const triggers = menu.querySelectorAll('[data-mobile-mega-trigger]');
    const panels = menu.querySelectorAll('[data-mobile-mega-panel]');

    const closeCategory = () => {
      if (!nav) return;
      nav.classList.remove('is-category-open');
      menu.removeAttribute('data-category-open');
      triggers.forEach((t) => {
        t.classList.remove('is-mega-active');
        t.setAttribute('aria-expanded', 'false');
      });
      panels.forEach((p) => {
        p.hidden = true;
      });
    };

    const openCategory = (index) => {
      if (!nav) return;
      nav.classList.add('is-category-open');
      menu.setAttribute('data-category-open', 'true');
      triggers.forEach((t) => {
        const isActive = t.dataset.megaIndex === index;
        t.classList.toggle('is-mega-active', isActive);
        t.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });
      panels.forEach((p) => {
        p.hidden = p.dataset.megaIndex !== index;
      });
    };

    triggers.forEach((t) => {
      t.addEventListener('click', () => {
        const index = t.dataset.megaIndex;
        if (t.classList.contains('is-mega-active')) {
          closeCategory();
        } else {
          openCategory(index);
        }
      });
    });

    const open = () => {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      document.body.classList.add('mobile-menu-open');
      lockScroll();
      toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    };

    const close = () => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mobile-menu-open');
      unlockScroll();
      toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      closeCategory();
    };

    toggles.forEach((t) => t.addEventListener('click', open));
    closers.forEach((c) => c.addEventListener('click', close));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) close();
    });
  };

  // --------------------------------------------------------------------
  // Auto-submit localization selectors on change
  // --------------------------------------------------------------------
  const initLocaleSelectors = () => {
    document.querySelectorAll('[data-locale-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });
  };

  // --------------------------------------------------------------------
  // Desktop mega menu — click-to-pin
  // Hovering still opens panels (CSS), but clicking a parent link "pins" it
  // open so the user can navigate the dropdown without the bar following the
  // cursor onto neighbouring triggers. Clicking the SAME pinned trigger
  // again navigates to the parent collection. Outside click / Escape closes.
  // --------------------------------------------------------------------
  const initDesktopMega = () => {
    const triggers = document.querySelectorAll('[data-mega-trigger]');
    if (!triggers.length) return;

    const menu = triggers[0].closest('.susmies-header__menu');
    if (!menu) return;

    let pinnedItem = null;

    const closePinned = () => {
      if (!pinnedItem) return;
      const panel = pinnedItem.querySelector('[data-mega-panel]');
      if (panel) panel.classList.remove('is-open');
      const trig = pinnedItem.querySelector('[data-mega-trigger]');
      if (trig) trig.setAttribute('aria-expanded', 'false');
      pinnedItem.classList.remove('is-pinned');
      pinnedItem = null;
      menu.removeAttribute('data-mega-pinned');
    };

    const pinItem = (item) => {
      closePinned();
      const panel = item.querySelector('[data-mega-panel]');
      const trig = item.querySelector('[data-mega-trigger]');
      if (panel) panel.classList.add('is-open');
      if (trig) trig.setAttribute('aria-expanded', 'true');
      item.classList.add('is-pinned');
      pinnedItem = item;
      menu.setAttribute('data-mega-pinned', 'true');
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        const item = trigger.closest('.susmies-header__menu-item');
        if (!item) return;
        if (pinnedItem === item) {
          // Second click on the already-pinned trigger: allow navigation.
          closePinned();
          return;
        }
        event.preventDefault();
        pinItem(item);
      });
    });

    document.addEventListener('click', (event) => {
      if (!pinnedItem) return;
      if (pinnedItem.contains(event.target)) return;
      closePinned();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePinned();
    });
  };

  const boot = () => {
    initScrollState();
    initAnnouncementHeight();
    initSubnav();
    initMobileMenu();
    initDesktopMega();
    initLocaleSelectors();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on theme editor section reload.
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-header]')) boot();
  });
})();
